import { DEFAULT_ROLE, getRoleByName } from '../constants/roles.js';

/**
 * User roles database structure for Google Sheets
 * Sheet Name: "UserRoles"
 * Columns: Email | Role | Status | CreatedDate | LastUpdated | Notes
 */

/**
 * Fetch user role from the database (Google Sheets)
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - User role data
 */
export const fetchUserRole = async (email) => {
  try {
    console.log('🔍 fetchUserRole called for:', email);
    
    // First try Google Sheets
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'production' 
        ? '/.netlify/functions/proxy' 
        : 'http://localhost:3001/api/proxy'
      }?endpoint=getUserRole&email=${encodeURIComponent(email)}`, { signal: AbortSignal.timeout(8000) });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 fetchUserRole response from Google Sheets:', data);
        if (data && data.success && data.data) {
          return data.data;
        }
        // success:false means script returned empty (403/not found) — not an error, just not in DB
      }
    } catch (sheetError) {
      console.log('⚠️ Google Sheets not available, checking localStorage fallback');
    }
    
    // Fallback to localStorage if Google Sheets fails
    const storedUsers = JSON.parse(localStorage.getItem('satellite_users') || '{}');
    console.log('📊 Checking localStorage users:', Object.keys(storedUsers));
    
    if (storedUsers[email]) {
      console.log('✅ Found user in localStorage:', storedUsers[email]);
      return storedUsers[email];
    }
    
    console.log('❌ User not found in either Google Sheets or localStorage');
    return null;
  } catch (error) {
    console.error('❌ Error fetching user role:', error);
    return null;
  }
};

/**
 * Get user role with fallback to default
 * @param {string} email - User's email address
 * @returns {Promise<string>} - User role name
 */
export const getUserRole = async (email) => {
  try {
    console.log('🔍 getUserRole called for:', email);
    const userData = await fetchUserRole(email);
    console.log('📊 fetchUserRole returned:', userData);
    
    if (userData) {
      const userRole = userData.role || userData.Role;
      const userStatus = userData.status || userData.Status;

      if (userRole && userStatus === 'active') {
        console.log('✅ Found active user role:', userRole);
        return userRole;
      }
    }

    // Fallback: authorize by domain
    const authorizedDomains = ['satellite-talent.com', 'talantascout.com'];
    const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean);
    const userDomain = email.split('@')[1];

    if (authorizedDomains.includes(userDomain)) {
      const isAdmin = adminEmails.includes(email);
      const role = isAdmin ? 'admin' : 'talent-partner';
      console.log(`✅ Domain-authorized user ${email} as ${role}`);
      return role;
    }

    // SECURITY: User not found in database - access denied
    console.log('❌ User not found in database - access denied:', email);
    throw new Error('Access denied: User not authorized. Please contact an administrator to add your account.');
  } catch (error) {
    console.error('❌ Error getting user role:', error);
    if (error.message.includes('Access denied')) {
      throw error;
    }
    throw new Error('Unable to verify user permissions');
  }
};

/**
 * Create a new user role entry in the database
 * @param {string} email - User's email address
 * @param {string} role - Role name
 * @param {string} notes - Optional notes (ignored in simplified format)
 * @returns {Promise<boolean>} - Success status
 */
export const createUserRole = async (email, role, notes = '') => {
  try {
    console.log('🔄 createUserRole called:', { email, role });
    
    // First try to create in Google Sheets
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'production' 
        ? '/.netlify/functions/proxy' 
        : 'http://localhost:3001/api/proxy'
      }`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: 'createUserRole',
          data: {
            email,
            role,
            status: 'active'
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ User created successfully in Google Sheets:', data);
        return true;
      }
    } catch (sheetError) {
      console.log('⚠️ Google Sheets not available, saving to localStorage fallback');
    }
    
    // Fallback to localStorage if Google Sheets fails
    const storedUsers = JSON.parse(localStorage.getItem('satellite_users') || '{}');
    
    const userData = {
      email,
      role,
      status: 'active',
      createdDate: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      notes: notes || ''
    };
    
    storedUsers[email] = userData;
    localStorage.setItem('satellite_users', JSON.stringify(storedUsers));
    
    console.log('✅ User created successfully in localStorage fallback:', userData);
    return true;
  } catch (error) {
    console.error('❌ Error creating user role:', error);
    return false;
  }
};

/**
 * Update an existing user role
 * @param {string} email - User's email address
 * @param {string} newRole - New role name
 * @param {string} notes - Optional notes (ignored in simplified format)
 * @returns {Promise<boolean>} - Success status
 */
export const updateUserRole = async (email, newRole, notes = '') => {
  try {
    const response = await fetch(`${process.env.NODE_ENV === 'production' 
      ? '/.netlify/functions/proxy' 
      : 'http://localhost:3001/api/proxy'
    }`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: 'updateUserRole',
        data: {
          email,
          role: newRole
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user role:', error);
    return false;
  }
};

/**
 * Deactivate a user (set status to inactive)
 * @param {string} email - User's email address
 * @param {string} reason - Reason for deactivation (ignored in simplified format)
 * @returns {Promise<boolean>} - Success status
 */
export const deactivateUser = async (email, reason = '') => {
  try {
    const response = await fetch(`${process.env.NODE_ENV === 'production' 
      ? '/.netlify/functions/proxy' 
      : 'http://localhost:3001/api/proxy'
    }`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: 'updateUserRole',
        data: {
          email,
          status: 'inactive'
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error deactivating user:', error);
    return false;
  }
};

/**
 * Get all users from the database
 * @returns {Promise<Array>} - Array of user role data
 */
export const getAllUsers = async () => {
  try {
    console.log('🔍 getAllUsers called');
    
    // For now, use localStorage as a simple user database
    const storedUsers = JSON.parse(localStorage.getItem('satellite_users') || '{}');
    const usersList = Object.values(storedUsers);
    
    console.log('📊 All users from local storage:', usersList);
    return usersList;
  } catch (error) {
    console.error('❌ Error fetching all users:', error);
    return [];
  }
};

/**
 * Validate if a role name is valid
 * @param {string} roleName - Role name to validate
 * @returns {boolean} - Whether the role is valid
 */
export const isValidRole = (roleName) => {
  const validRoles = ['admin', 'talent-partner', 'client'];
  return validRoles.includes(roleName);
};

/**
 * Get user data with role information
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - Complete user data with role and permissions
 */
export const getUserData = async (email) => {
  try {
    const roleName = await getUserRole(email);
    const role = getRoleByName(roleName);
    
    return {
      email,
      role: roleName,
      displayRole: role.displayName,
      permissions: role.permissions,
      isActive: true
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    return {
      email,
      role: DEFAULT_ROLE,
      displayRole: getRoleByName(DEFAULT_ROLE).displayName,
      permissions: getRoleByName(DEFAULT_ROLE).permissions,
      isActive: false
    };
  }
};
